import {
  DesktopContainer,
  LoadingSpinner,
  WarrantyInfo,
} from '@rabbit/elements/shared-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { useConsumerHoldingEditor, useGetConsumerHoldings } from '@rabbit/bizproc/react';
import { useMyConsumerPersona } from '@rabbit/data/portal';
import { getWarrantyInfo } from '@rabbit/olive/utils/helpers';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppContext } from '@rabbit/app-context';
import { BL_Warranty } from '@rabbit/bizproc/core';
import { resolveWarrantyContent } from '@rabbit/bizproc/react';
import { Config_WarrantyContentSource, Config_WarrantyMergeStrategy } from '@rabbit/config/enums';
import { FBD_Warranty } from '@rabbit/data/types';

/* eslint-disable-next-line */
export interface HoldingSingleWarrantyInfoViewProps {}

export interface ItemProps {
  id?: string;
  title: string;
  description?: string;
  included?: string;
}

export interface InfoInterface {
  title: string;
  type: string;
  items?: ItemProps[];
  children?: any;
}

export function HoldingSingleWarrantyInfoView(
  props: HoldingSingleWarrantyInfoViewProps
) {
  const { config } = useContext(AppContext);
  const location = useLocation();
  const holdingId = location.pathname.split('/')[2];
  const persona = useMyConsumerPersona();
  const { t } = useTranslation();
  const { body } = useConsumerHoldingEditor(
    persona?.personaId ?? '',
    holdingId
  );
  const { getCurrentHoldingProxy, getSingleHolding, getVendableByHoldingLink } = useGetConsumerHoldings(persona.personaId ?? '')

  if (!body) return <LoadingSpinner size="md" extraClasses="mt-5" />;

  const navigate = useNavigate();
  const { tenantInfo } = useContext(AppContext);
  const [warrantyInfo, setWarrantyInfo] = useState<InfoInterface[]>([]);
  const [warrantyTerms, setWarrantyTerms] = useState<InfoInterface[]>([]);
  const [holdingBrand, setHoldingBrand] = useState<string | undefined>(undefined);

  // Get holding summary to extract brand information
  useEffect(() => {
    const fetchHoldingData = async () => {
      try {
        const holdingSummary = await getSingleHolding(holdingId);
        if (holdingSummary && !(holdingSummary instanceof Error)) {
          // Set brand to undefined if it's null, empty, or undefined
          const brand = holdingSummary.brand;
          setHoldingBrand(brand && typeof brand === 'string' && brand.trim() !== '' ? brand : undefined);
        } else {
          setHoldingBrand(undefined);
        }
      } catch (error) {
        console.error('Error fetching holding data:', error);
        setHoldingBrand(undefined);
      }
    };
    
    fetchHoldingData();
  }, [holdingId, getSingleHolding]);

  useEffect(() => {
    const getWarranty = async () => {
      try {
        // Pass brand information for brand-specific warranty terms
        const tenantLink = t('tenantLink');
        const warranty = await getWarrantyInfo(tenantLink, holdingBrand);

        // Vendable overrides (if available)
        let vendableInfoOverride: InfoInterface[] = [];
        let vendableTermsOverride: InfoInterface[] = [];
        try {
          const proxy = await getCurrentHoldingProxy(holdingId);
          if (proxy && !(proxy instanceof Error) && (proxy as any).holdingLink) {
            const vendable = await getVendableByHoldingLink((proxy as any).holdingLink);
            const vPub = (vendable as any)?.pub || vendable;
            vendableInfoOverride = (vPub?.warrantyInfoOverride as InfoInterface[]) || [];
            vendableTermsOverride = (vPub?.warrantyTermsOverride as InfoInterface[]) || [];
          }
        } catch (e) {
          console.error('Error fetching Product details:', e);
        }

        const fetchTemplateContent = async (): Promise<{ info: InfoInterface[]; terms: InfoInterface[] }> => {
          if (config.WARRANTIES.WARRANTY_TC_FROM_FIRESTORE) {
            const currentHoldingProxy = await getCurrentHoldingProxy(holdingId);
            if (currentHoldingProxy && !(currentHoldingProxy instanceof Error)) {
              const latestWarranty = BL_Warranty.getLatestWarranty(currentHoldingProxy.warranties ?? []);
              if (latestWarranty?.warrantyLink) {
                const latestWarrantyTermsInfo = await FBD_Warranty.get(latestWarranty.warrantyLink);
                if (latestWarrantyTermsInfo?.snapshot) {
                  return {
                    info: (latestWarrantyTermsInfo.snapshot.info as InfoInterface[]) || [],
                    terms: (latestWarrantyTermsInfo.snapshot.terms as InfoInterface[]) || [],
                  };
                }
              }
            }
          }
          return {
            info: (warranty?.warranty_info as InfoInterface[]) || [],
            terms: (warranty?.warranty_terms as InfoInterface[]) || [],
          };
        };

        const { info, terms } = await resolveWarrantyContent({
          source: config.WARRANTIES.WARRANTY_TC_CONTENT_SOURCE || Config_WarrantyContentSource.TEMPLATE,
          mergeStrategy: config.WARRANTIES.MERGE_STRATEGY || Config_WarrantyMergeStrategy.APPEND,
          vendableInfoOverride,
          vendableTermsOverride,
          fetchTemplateContent,
        });

        setWarrantyInfo(Array.isArray(info) ? info : []);
        setWarrantyTerms(Array.isArray(terms) ? terms : []);
      } catch (error) {
        console.error('Error retrieving warranty information:', error);
      }
    };

    // Fetch warranty info - holdingBrand can be undefined (which is fine)
    getWarranty();
  }, [holdingId, persona.personaId, config.WARRANTIES.WARRANTY_TC_FROM_FIRESTORE, t, holdingBrand]);

  return (
    <div className="flex flex-wrap items-start justify-center">
      <div className="w-full md:basis-1/2">
        <DesktopContainer
          title={
            tenantInfo?.name === 'NuCover'
              ? t('general.carePlanSummary')
              : t('general.warrantyInformation')
          }
          goBack={() => navigate(-1)}
        >
          {warrantyInfo
            .filter(
              (data: InfoInterface) => data.title !== 'Consumer Guarantee'
            )
            .map((data: InfoInterface, index: number) => (
              <WarrantyInfo key={index} data={data} />
            ))}
        </DesktopContainer>
      </div>
      {warrantyTerms?.length !== 0 && (
        <div className="w-full md:basis-1/2">
          <DesktopContainer title={'Warranty terms'}>
            {warrantyTerms.map((data: InfoInterface, index: number) => (
              <WarrantyInfo key={index} data={data} />
            ))}
          </DesktopContainer>
        </div>
      )}
      <div className="pt-8 lg:px-8">
        {warrantyInfo
          .filter((data: InfoInterface) => data.title === 'Consumer Guarantee')
          .map((data: InfoInterface, index: number) => (
            <WarrantyInfo key={index} data={data} />
          ))}
      </div>
    </div>
  );
}

export default HoldingSingleWarrantyInfoView;
